// @flow

import ReduceStore from 'PMT/flux/ReduceStore';
import _ from 'lodash';

import SessionStore from './SessionStore';
import RouteStore from './RouteStore';
import RoutingEnum from 'constants/RoutingEnum';

// -- constant
import Events from 'constants/Events';
import ActionTypes from 'constants/ActionTypes';

import OrderSettingsStore from './OrderSettingsStore';

// -- entity
import ApiError from 'PMT/entities/ApiError';
import { Restaurant } from 'PMT/entities';
import { RestaurantsGroup } from 'PMT/entities/restaurantsGroup';
import { ProRolesList } from 'PMT/entities/security';


function updateRestaurantsGroups(nextState, restaurantsGroupToUpdate: RestaurantsGroup) {
  nextState.restaurantsGroup.updateRestaurantsGroups(restaurantsGroupToUpdate);
  nextState.selectedRestaurantsGroup.updateRestaurantsGroups(restaurantsGroupToUpdate);

  return nextState;
}

/**
 * Handle the current selected restaurant and it's associate restaurant group.
 */
class RestaurantStore extends ReduceStore {

  getInitialState() {
    return {
      /**
      * @type Restaurant
      */
      restaurant: null,

      /**
      * @type Restaurant
      */
      createdRestaurant: null,

      /**
      * @type ApiError
      */
      apiError: null,

      /**
      * @type RestaurantsGroup
      */
      restaurantsGroup: null,

      /**
      * @type RestaurantsGroup
      */
      selectedRestaurantsGroup: null,

      /**
      * @type RestaurantsGroup
      */
      updatedRestaurantsGroup: null,

      /**
      * @type ProRolesList
      */
      rolesList: null,

      /**
      * @type RestaurantsGroup
      */
      newRestaurantsGroup: null,
    }
  }

  isEmitingCustomEvents(): boolean {
    return true;
  }

  getStateObjectsMap() {
    return {
      apiError: new ApiError(),
      restaurant: new Restaurant(),
      createdRestaurant: new Restaurant(),
      restaurantsGroup: new RestaurantsGroup(),
      selectedRestaurantsGroup: new RestaurantsGroup(),
      updatedRestaurantsGroup: new RestaurantsGroup(),
      rolesList: new ProRolesList(),
      newRestaurantsGroup: new RestaurantsGroup(),
    }
  }

  resetState(nextState) {
    nextState.restaurant = null;
    nextState.createdRestaurant = null;
    nextState.apiError = null;
    // nextState.restaurantsGroup = null;
    nextState.selectedRestaurantsGroup = null;
    nextState.updatedRestaurantsGroup = null;
    // nextState.rolesList = null;
    nextState.newRestaurantsGroup = null;

    return nextState;
  }

  getApiError(): ApiError {
    return this._state.apiError;
  }

  getRestaurant(): Restaurant {
    return this._state.restaurant;
  }

  getCreatedRestaurant(): Restaurant {
    return this._state.createdRestaurant;
  }

  getUpdatedRestaurantsGroup(): RestaurantsGroup {
    return this._state.updatedRestaurantsGroup;
  }

  getRestaurantId(): ?string {
    if (!_.isNull(this._state.restaurant)) {
      return this._state.restaurant.id;
    }

    return null;
  }

  /**
   * Save the parent restaurantsGroup.
   * If there is no selected restaurant group, we set by default the saved RestaurantsGroup id on
   * session, otherwise,we use the parent.
   */
  setRestaurantsGroup(nextState, restaurantsGroup) {
    nextState.restaurantsGroup = restaurantsGroup;

    // set default _selectedRestaurantsGroup
    if (_.isNull(nextState.selectedRestaurantsGroup)) {
      nextState.selectedRestaurantsGroup =
      nextState.restaurantsGroup.findWithId(SessionStore.getRestaurantsGroupId());

      // there is no selected restaurant group before, or its not found, we set the firt
      // restaurants group.
      if (_.isNull(nextState.selectedRestaurantsGroup)) {
        nextState.selectedRestaurantsGroup = nextState.restaurantsGroup;
      }
    }

    return nextState;
  }

  getRestaurantsGroup(): RestaurantsGroup {
    return this._state.restaurantsGroup;
  }

  getSelectedRestaurantsGroup(): RestaurantsGroup {
    return this._state.selectedRestaurantsGroup;
  }

  getRolesList(): ProRolesList {
    return this._state.rolesList;
  }

  reduce(state, action, nextState) {
    switch (action.type) {

      /**
       * On logout we reset our store objects.
       */
      case ActionTypes.LOGOUT:
        return this.resetState(nextState);

      case ActionTypes.PRO_ACCOUNT_GET_ME_SUCCESS:
        this.waitFor([
          OrderSettingsStore.getDispatchToken(),
        ]);
        nextState.restaurant = action.restaurant;
        SessionStore.saveRestaurantId(nextState.restaurant.id);
        // Resolve the saved subgroup before persisting selection, not the domain root.
        this.setRestaurantsGroup(nextState, action.proAccount.restaurantsGroup);
        SessionStore.saveRestaurantsGroupId(nextState.selectedRestaurantsGroup.id);
        return nextState;

      case ActionTypes.GET_RESTAURANT_SUCCESS:
        this.waitFor([
          OrderSettingsStore.getDispatchToken(),
        ]);
        nextState.restaurant = action.restaurant;

        this.emitEvent(Events.LOAD_RESTAURANT_SUCCESS);
        return nextState;

      case ActionTypes.PUT_RESTAURANT_MULTIPART_RESPONSE_SUCCESS:
        nextState.restaurant = action.restaurant;

        this.emitEvent(Events.UPDATE_RESTAURANT_SUCCESS);
        return nextState;

      case ActionTypes.PUT_RESTAURANT_MULTIPART_RESPONSE_FAILURE:
        nextState.apiError = action.apiError;

        this.emitEvent(Events.UPDATE_RESTAURANT_FAILURE);
        return nextState;

      case ActionTypes.GET_RESTAURANT_FAILURE:
        nextState.apiError = action.apiError;

        this.emitEvent(Events.LOAD_RESTAURANT_FAILURE);
        this.emitEvent(Events.END_LOADING);
        return nextState;

      case ActionTypes.POST_RESTAURANT_RESPONSE_SUCCESS:
        nextState.createdRestaurant = action.restaurant;
        nextState.restaurantsGroup = action.restaurantsGroup;

        nextState.apiError = null;
        this.emitEvent(Events.CREATE_RESTAURANT_SUCCESS);
        return nextState;

      case ActionTypes.POST_RESTAURANT_RESPONSE_FAILURE:
        nextState.apiError = action.apiError;

        this.emitEvent(Events.CREATE_RESTAURANT_FAILURE);
        return nextState;

      //
      // RestaurantsGroup - GET
      //

      case ActionTypes.GET_RESTAURANTS_GROUP_SUCCESS:
        nextState.apiError = null;
        this.emitEvent(Events.LOAD_RESTAURANTS_GROUP_SUCCESS);
        return this.setRestaurantsGroup(nextState, action.restaurantsGroup);

      case ActionTypes.GET_RESTAURANTS_GROUP_FAILURE:
        nextState.apiError = action.apiError;

        this.emitEvent(Events.LOAD_RESTAURANTS_GROUP_FAILURE);
        return nextState;

      //
      // RestaurantsGroup - POST
      //

      case ActionTypes.POST_RESTAURANTS_GROUP_SUCCESS:
        nextState.newRestaurantsGroup = action.restaurantsGroup;

        this.emitEvent(Events.CREATE_RESTAURANTS_GROUP_SUCCESS);
        return updateRestaurantsGroups(nextState, nextState.newRestaurantsGroup);

      case ActionTypes.POST_RESTAURANTS_GROUP_FAILURE:
        nextState.apiError = action.apiError;

        this.emitEvent(Events.CREATE_RESTAURANTS_GROUP_FAILURE);
        return nextState;

      //
      // RestaurantsGroup - PUT
      //

      case ActionTypes.PUT_RESTAURANTS_GROUP_SUCCESS:
        nextState.updatedRestaurantsGroup = action.restaurantsGroup;

        this.emitEvent(Events.UPDATE_RESTAURANTS_GROUP_SUCCESS);
        return updateRestaurantsGroups(nextState, nextState.updatedRestaurantsGroup);

      case ActionTypes.PUT_RESTAURANTS_GROUP_FAILURE:
        nextState.apiError = action.apiError;

        this.emitEvent(Events.UPDATE_RESTAURANTS_GROUP_FAILURE);
        return nextState;

      //
      // Choose current restaurant
      //

      /**
       * Send an event during the request for the Layout to be prepared for the changes.
       */
      case ActionTypes.GET_RESTAURANT_FOR_GROUP_REQUEST:
        this.emitEvent(Events.RESTAURANT_CHANGE_REQUEST);
        return this.resetState(nextState);

      case ActionTypes.GET_RESTAURANT_FOR_GROUP_SUCCESS:
        nextState.selectedRestaurantsGroup = action.restaurantsGroup;
        nextState.restaurant = action.restaurant;

        SessionStore.saveRestaurantsGroupId(action.restaurantsGroup.id);
        SessionStore.saveRestaurantId(nextState.restaurant.id);

        nextState.apiError = null;


        // redirect to DASHBOARD, unless the restaurant is not configured.
        if (action.restaurant.features !== 0) {
          RouteStore.redirectTo(RoutingEnum.DASHBOARD);
        } else {
          RouteStore.redirectTo(RoutingEnum.RESTAURANT_EDIT);
        }

        this.emitEvent(Events.RESTAURANT_CHANGE_SUCCESS);
        this.emitEvent(Events.LOAD_RESTAURANT_SUCCESS);
        return nextState;

      case ActionTypes.GET_RESTAURANT_FOR_GROUP_FAILURE:
        nextState.apiError = action.apiError;
        // TODO
        return nextState;

      //
      // RestaurantGroupsRole - GET
      //

      case ActionTypes.GET_RESTAURANT_ROLES_SUCCESS:
        nextState.apiError = null;

        nextState.rolesList = action.proRolesList;

        this.emitEvent(Events.LOAD_RESTAURANT_ROLES_SUCCESS);
        return nextState;

      case ActionTypes.GET_RESTAURANT_ROLES_FAILURE:
        nextState.apiError = action.apiError;
        this.emitEvent(Events.LOAD_RESTAURANT_ROLES_FAILURE);
        return nextState;

      //
      // ----------------- ROLES
      //

      //
      // Update
      //

      case ActionTypes.PUT_ROLE_RESPONSE_SUCCESS:
        nextState.rolesList.update(action.proRole);
        this.emitEvent(Events.UPDATE_ROLE_SUCCESS);
        return nextState;

      case ActionTypes.PUT_ROLE_RESPONSE_FAILURE:
        // TODO
        this.emitEvent(Events.UPDATE_ROLE_FAILURE);
        return nextState;

      //
      // Create
      //

      case ActionTypes.POST_ROLE_RESPONSE_SUCCESS:
        if (_.isNull(nextState.rolesList)) {
          nextState.rolesList = new ProRolesList();
        }
        nextState.rolesList.push(action.proRole);
        this.emitEvent(Events.CREATE_ROLE_SUCCESS);
        return nextState;

      case ActionTypes.POST_ROLE_RESPONSE_FAILURE:
        // TODO
        this.emitEvent(Events.CREATE_ROLE_FAILURE);
        return nextState;


      //
      // Delete
      //

      case ActionTypes.DELETE_ROLE_RESPONSE_SUCCESS:
        nextState.rolesList.remove(action.proRole);
        this.emitEvent(Events.DELETE_ROLE_SUCCESS);
        return nextState;

      case ActionTypes.DELETE_ROLE_RESPONSE_FAILURE:
        // TODO
        this.emitEvent(Events.DELETE_ROLE_FAILURE);
        return nextState;

      default:
        return nextState;
    }
  }
}

export default new RestaurantStore();
